import axios from "axios";
import useUserStore from "../context/useUserStore";
import storage from "../utils/storage";
import { API } from "../utils/constants";

const API_BASE_URL = "/api";

/**
 * session.js — shared auth-session recovery for the 401 → silent-refresh →
 * retry → re-login flow (SPR-007 + DEF-015).
 *
 * Both the mutation path (`useApi`) and the SWR read path (`authFetcher`) must
 * recover from an expired access token the same way. To avoid drift, the actual
 * refresh + session-clear + redirect logic lives here, and both callers reuse
 * it. `authFetcher` runs outside React, so the router-aware redirect is supplied
 * by a tiny component (SessionRedirectBridge) that registers a handler here.
 */

// Registered by SessionRedirectBridge (App-level) so non-React callers can
// route to /login through React Router.
let redirectHandler = null;

/**
 * registerRedirectToLogin — Stores the router-aware redirect callback.
 * @param {(from: string|null) => void} fn
 * @returns {() => void} unregister
 */
export function registerRedirectToLogin(fn) {
  redirectHandler = fn;
  return () => {
    if (redirectHandler === fn) redirectHandler = null;
  };
}

/**
 * redirectToLogin — Routes to /login carrying the current location as
 * `state.from` (so re-login can return the user there). Falls back to a hard
 * location change if no router handler is registered. Auth routes are never
 * stashed as the return target (no redirect loops).
 */
export function redirectToLogin() {
  const path =
    typeof window !== "undefined" ? window.location?.pathname ?? "/" : "/";
  const search =
    typeof window !== "undefined" ? window.location?.search ?? "" : "";
  const isAuthRoute = path === "/login" || path === "/change-password";
  const from = isAuthRoute ? null : `${path}${search}`;

  if (redirectHandler) {
    redirectHandler(from);
  } else if (typeof window !== "undefined") {
    window.location.assign("/login");
  }
}

/**
 * clearSession — Clears the in-memory + persisted auth session.
 */
export function clearSession() {
  const { setAccessToken, setCurrentUser } = useUserStore.getState();
  setCurrentUser(undefined);
  setAccessToken(null);
  storage.removeItem(storage.ITEMS.currentUser);
  storage.removeItem(storage.ITEMS.jwt);
}

/**
 * attemptRefresh — Single silent token refresh (httpOnly refresh cookie).
 * On success, persists the new access token (store + storage) and returns it.
 * On failure, returns null (caller decides whether to clear/redirect).
 *
 * @returns {Promise<string|null>} the new access token, or null on failure
 */
export async function attemptRefresh() {
  try {
    const res = await axios.post(
      `${API_BASE_URL}${API.AUTH.REFRESH}`,
      {},
      { withCredentials: true }
    );
    const newToken = res.data?.accessToken;
    if (newToken) {
      useUserStore.getState().setAccessToken(newToken);
      storage.setItem(storage.ITEMS.jwt, newToken);
      return newToken;
    }
    return null;
  } catch {
    return null;
  }
}
